import { useState, useEffect, useMemo } from "react";
import SearchSelect from "./SearchSelect";
import { loadFromSupabase } from "../supabaseDB";
import ShipperGrowers from "./shipper/ShipperGrowers";

/* ============================================================================
 * GrowerRegistry (admin) — wrapper around ShipperGrowers.
 *
 * Per spec, every V2 grower form/list improvement the shipper now sees
 * must also be available in the admin. Rather than maintain two copies
 * of a ~550-line form + compliance evaluator + ranchos editor, the
 * admin module here just picks a shipper from a dropdown and then
 * renders the same ShipperGrowers component scoped to that shipper.
 * The shipper-side already handles:
 *   - V2 identity / producer code (3 digits) / season fields
 *   - Structured rancho address (Calle / Municipio / Ciudad / Estado
 *     / CP / País / Notas / Hectáreas)
 *   - Contact phone + email
 *   - Required-cert RED/GREEN status based on uploaded documents
 *   - Compliance semáforo column in the list
 *   - Conditional delete vs deactivate based on active-season
 *     shipments
 *   - Linked-Shipper auto-attached via the supplierId prop
 *
 * The admin view simply chooses WHICH shipper to step into. To browse
 * "every grower across every shipper" admin still has the rest of the
 * app (search, filters per-shipper). A "view all" mode could be
 * grafted on later if Arturo asks for it.
 * ============================================================================ */

const T = {
  en: {
    title: "Growers",
    sub: "Pick a shipper to manage their growers.",
    selectShipper: "Select a shipper…",
    noShipper: "Pick a shipper above to load their growers.",
    showing: "Showing growers for",
  },
  es: {
    title: "Productores",
    sub: "Elige un proveedor para administrar sus productores.",
    selectShipper: "Selecciona un proveedor…",
    noShipper: "Elige un proveedor arriba para cargar sus productores.",
    showing: "Mostrando productores de",
  },
};

export default function GrowerRegistry({ lang: globalLang }) {
  const lang = globalLang || "es";
  const t = T[lang === "en" ? "en" : "es"];
  const [suppliers, setSuppliers] = useState([]);
  const [pickedId, setPickedId] = useState(() => {
    try { return localStorage.getItem("gfi-grower-admin-shipper") || ""; } catch { return ""; }
  });

  useEffect(() => {
    loadFromSupabase("suppliers")
      .then(rows => setSuppliers((rows || []).filter(s => s.isActive !== false)))
      .catch(() => setSuppliers([]));
  }, []);

  useEffect(() => {
    try { localStorage.setItem("gfi-grower-admin-shipper", pickedId || ""); } catch (e) {}
  }, [pickedId]);

  const options = useMemo(() => suppliers.map(s => ({
    value: s.id,
    label: (s.shortName || s.fullName || s.name || s.id) + (s.initials ? " (" + s.initials + ")" : ""),
  })), [suppliers]);

  const pickedSupplier = useMemo(() => suppliers.find(s => String(s.id) === String(pickedId)) || null, [suppliers, pickedId]);

  return (
    <div>
      <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: "0 0 auto" }}>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{t.title}</h2>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--color-text-muted)" }}>{t.sub}</p>
        </div>
        <div style={{ flex: 1, minWidth: 240, maxWidth: 360 }}>
          <SearchSelect
            value={pickedId}
            onChange={v => setPickedId(v || "")}
            placeholder={t.selectShipper}
            options={options}
          />
        </div>
        {pickedSupplier && (
          <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
            {t.showing} <strong>{pickedSupplier.shortName || pickedSupplier.fullName}</strong>
          </div>
        )}
      </div>

      {pickedId ? (
        /* ShipperGrowers handles every V2 affordance the spec asks
           for. supplierId is the legacy string id (record.id). */
        <ShipperGrowers
          supplierId={pickedId}
          supplier={pickedSupplier}
          profile={null}
          lang={lang}
        />
      ) : (
        <div style={{ padding: "60px 20px", textAlign: "center", color: "var(--color-text-muted)", fontSize: 13, fontStyle: "italic" }}>
          {t.noShipper}
        </div>
      )}
    </div>
  );
}
